<template>
  <section
    id="cookieConsent"
    class="flex flex-col items-start gap-2 self-stretch"
  >
    <div class="flex items-center gap-2 self-stretch">
      <UISwitch
        id="checkForCookieConsent"
        :value="enabled"
        @update:value="onToggle"
      />
      <div class="flex items-center gap-1">
        <UITextSmNormal>{{ t("form.checkForCookieConsent") }}</UITextSmNormal>
        <UITooltip
          :style="{ maxWidth: '300px' }"
          trigger="hover"
          placement="top"
        >
          <template #trigger>
            <HelpCircleIcon class="h-4 w-4 text-gray-400 cursor-pointer" />
          </template>
          {{ t("form.checkForCookieConsentTooltip") }}
        </UITooltip>
      </div>
    </div>
    <div v-if="enabled" class="flex flex-col gap-1 w-[58%] mt-1">
      <UITextSmNormal>{{ t("form.cookieConsentProvider") }}</UITextSmNormal>
      <UISelect
        id="cookieConsentProvider"
        size="medium"
        :value="provider"
        :options="cookieConsentProviderOptions"
        :placeholder="t('form.cookieConsentProviderPlaceholder')"
        @change="onProviderChange"
      />
    </div>
  </section>
</template>

<script setup lang="ts">
import { COOKIE_CONSENT_PROVIDER_DEFAULT } from "@/util/constants";
import { HelpCircleIcon } from "@gohighlevel/ghl-icons/24/outline";
import {
  UISelect,
  UISwitch,
  UITextSmNormal,
  UITooltip,
} from "@gohighlevel/ghl-ui";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

interface Props {
  enabled: boolean;
  provider: string;
}

defineProps<Props>();

const emit = defineEmits<{
  (e: "update:enabled", value: boolean): void;
  (e: "update:provider", value: string): void;
}>();

const cookieConsentProviderOptions = [
  { label: t("form.cookieConsentProviderAutoDetect"), value: "auto" },
  { label: "GHL Cookie Consent", value: "ghl_cookie" },
  { label: "OneTrust", value: "onetrust", disabled: true }, // Disabled because currently they don't provide free trial so we can't test it.
  { label: "Complianz", value: "complianz" },
  { label: "Cookiebot", value: "cookiebot" },
  { label: "Usercentrics", value: "usercentrics" },
  { label: "TrustArc", value: "trustarc", disabled: true }, // Disabled because currently they don't provide free trial so we can't test it.
  { label: "IAB TCF", value: "tcf" },
  { label: "CookieYes", value: "cookieyes" },
  { label: "Iubenda", value: "iubenda" },
  { label: "Termly", value: "termly" },
  { label: "Osano", value: "osano", disabled: true }, // Disabled because currently they don't provide free trial so we can't test it.
  { label: "Didomi", value: "didomi", disabled: true }, // Disabled because currently they don't provide free trial so we can't test it.
];

const onToggle = (value: boolean) => {
  emit("update:enabled", value);
  if (!value) {
    emit("update:provider", COOKIE_CONSENT_PROVIDER_DEFAULT);
  }
};

const onProviderChange = (value: string) => {
  emit("update:provider", value);
};
</script>
